import type { Middleware } from 'openapi-fetch'

import { resolveBackendEnvironment } from '#app/composables/use-environment'
import { token } from '#app/store/auth'

export class ClientError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public body: unknown = null,
  ) {
    super(message)
    this.statusCode = statusCode
    this.body = body
  }
}

async function safeParseResponseBody(response: Response): Promise<unknown> {
  try {
    const text = await response.clone().text()
    if (!text) return null
    try {
      return JSON.parse(text)
    } catch {
      return text
    }
  } catch {
    return null
  }
}

export const authMiddleware: Middleware = {
  async onRequest({ request }) {
    if (token.value) request.headers.set('Authorization', `Bearer ${token.value}`)
    if (!request.headers.has('Content-Type')) {
      request.headers.set('Content-Type', 'application/json')
    }
    return request
  },
}

export const throwOnErrorMiddleware: Middleware = {
  async onResponse({ response }) {
    if (!response.ok && response.status !== 304) {
      const body = await safeParseResponseBody(response)
      throw new ClientError(
        `${response.url}: ${response.status} ${response.statusText}`,
        response.status,
        body,
      )
    }
  },
}

export function createEnvironmentRewriteMiddleware(bases: {
  acceptance: string
  production: string
}): Middleware {
  const acceptance = bases.acceptance.replace(/\/$/, '')
  const production = bases.production.replace(/\/$/, '')
  return {
    async onRequest({ request }) {
      const target = resolveBackendEnvironment() === 'acceptance' ? acceptance : production
      const source = [production, acceptance].find((base) => request.url.startsWith(base))
      if (!source || source === target) return request

      const url = target + request.url.slice(source.length)
      const hasBody = request.method !== 'GET' && request.method !== 'HEAD'
      return new Request(url, {
        method: request.method,
        headers: request.headers,
        body: hasBody ? await request.clone().arrayBuffer() : undefined,
        credentials: request.credentials,
        redirect: request.redirect,
        signal: request.signal,
      })
    },
  }
}

export const defaultMiddleware: Middleware[] = [authMiddleware, throwOnErrorMiddleware]
